import React, { useEffect, useState } from "react";
import apiClient from "../api/apiClient";

const Home2 = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [contactValues, setContactValues] = useState({
    name: "", email: "", subject: "", message: "", contact_guard_7f4c: "",
  });
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [projectsComingSoon, setProjectsComingSoon] = useState(true);

  useEffect(() => {
    let active = true;
    apiClient.getHome()
      .then((content) => active && setData(content))
      .catch(() => active && setError("Unable to load the portfolio."));
    apiClient.getProjects()
      .then((content) => active && setProjectsComingSoon(Boolean(content.comingSoon)))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const updateContact = (field, value) => setContactValues((current) => ({ ...current, [field]: value }));

  const submitContactForm = async (event) => {
    event.preventDefault();
    setContactBusy(true);
    setContactError("");
    setContactMessage("");
    try {
      const result = await apiClient.sendContactMessage(contactValues);
      if (result?.status !== "sent") throw new Error("The mail server did not confirm delivery.");
      setContactValues({ name: "", email: "", subject: "", message: "", contact_guard_7f4c: "" });
      setContactMessage("Message transmitted successfully.");
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setContactError(typeof detail === "string" ? detail : "Your message could not be sent. Please try again later.");
    } finally {
      setContactBusy(false);
    }
  };

  if (error) return <div className="page-shell py-20 text-center text-muted">{error}</div>;
  if (!data) return <div className="page-shell py-20 text-center font-mono text-xs uppercase tracking-widest text-muted">Loading portfolio…</div>;

  return (
    <div className="page-enter">
      <section className="grid-bg relative overflow-hidden border-b border-line">
        <div className="scan-line" />
        <span className="hud-corner left-5 top-5 border-l border-t" />
        <span className="hud-corner bottom-5 right-5 border-b border-r" />
        <div className="page-shell grid min-h-[650px] items-center gap-14 py-20 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,.65fr)] lg:py-28">
          <div className="relative z-10 max-w-4xl">
            <div className="mb-8 flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="eyebrow">System // Portfolio</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted">Status · Online</span>
            </div>
            <h1 className="font-display text-[clamp(3.4rem,9vw,8rem)] font-semibold leading-[.84] tracking-[-0.075em] text-textmain">
              {data.firstName}<br /><span className="text-accent">{data.lastName}</span>
            </h1>
            <p className="mt-8 max-w-2xl font-display text-xl leading-snug tracking-[-0.02em] text-textmain md:text-3xl">
              {data.profile}
            </p>
            <p className="mt-5 max-w-xl text-base leading-7 text-muted">{data.summary}</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
              Current // <span className="text-textmain">{data.currentRole.title}</span> · <span className="text-accent">{data.currentRole.company}</span>
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              {!projectsComingSoon && <a href="/work" className="btn-primary">View selected work ↗</a>}
              <a href="#contact" className="btn-ghost">Start a conversation</a>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-sm">
            <div className="absolute -inset-3 border border-line" />
            <div className="relative aspect-[4/5] overflow-hidden border border-line bg-surface2">
              <img src={data.image.src} alt={data.image.alt} className="h-full w-full object-cover grayscale-[18%]" loading="eager" />
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-bg/85 px-4 py-3 backdrop-blur">
                <span className="font-mono text-[9px] uppercase tracking-[.12em] text-muted">Portrait / 01</span>
                <span className="font-mono text-[9px] uppercase tracking-[.12em] text-accent">Rome · IT</span>
              </div>
            </div>
            <span className="absolute -right-3 -top-7 font-mono text-[8px] uppercase tracking-widest text-muted">Frame 0001</span>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-card">
        <div className="page-shell grid divide-y divide-line md:grid-cols-[170px_1fr] md:divide-x md:divide-y-0">
          <div className="flex items-center py-6 md:pr-8"><span className="eyebrow">Focus areas</span></div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 py-6 md:pl-8">
            {data.mainSkills.map((skill, index) => (
              <span key={skill} className="font-mono text-[10px] uppercase tracking-[0.1em] text-textmain">
                <span className="mr-2 text-accent">{String(index + 1).padStart(2, "0")}</span>{skill}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="page-shell py-24 md:py-32">
        <div className="mb-12 grid gap-5 border-b border-line pb-8 md:grid-cols-[150px_1fr]">
          <span className="section-index">01 / Capabilities</span>
          <div><p className="eyebrow mb-3">Selected disciplines</p><h2 className="section-title">Engineering across perception, software, and systems.</h2></div>
        </div>
        <div className="grid border-l border-t border-line md:grid-cols-3">
          {data.cards.map((card, index) => (
            <article key={card.title} className="group min-h-[270px] border-b border-r border-line bg-card p-7 transition hover:bg-surface2 md:p-9">
              <div className="flex items-center justify-between"><span className="font-mono text-[9px] text-muted">C/{String(index + 1).padStart(2, "0")}</span><span className="text-accent transition group-hover:translate-x-1">→</span></div>
              <h3 className="mt-14 font-display text-2xl font-semibold tracking-[-0.03em] text-textmain">{card.title}</h3>
              <p className="mt-4 text-sm leading-7 text-muted">{card.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-line bg-card">
        <div className="page-shell grid gap-12 py-24 md:grid-cols-[150px_1fr] md:py-28">
          <span className="section-index">02 / About</span>
          <div className="grid gap-12 lg:grid-cols-[.8fr_1.2fr]">
            <div><p className="eyebrow mb-3">Profile</p><h2 className="section-title">{data.aboutMe.title}</h2><p className="mt-6 font-display text-xl text-textmain">{data.aboutMe.subtitle}</p></div>
            <ol className="border-t border-line">
              {data.aboutMe.points.map((point, index) => (
                <li key={point} className="grid grid-cols-[42px_1fr] gap-4 border-b border-line py-5 text-sm leading-7 text-muted">
                  <span className="font-mono text-[9px] text-accent">{String(index + 1).padStart(2, "0")}</span><span>{point}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section id="contact" className="page-shell scroll-mt-24 py-24 md:py-32">
        <div className="mb-12 grid gap-5 border-b border-line pb-8 md:grid-cols-[150px_1fr]">
          <span className="section-index">03 / Contact</span>
          <div><p className="eyebrow mb-3">Open channel</p><h2 className="section-title">{data.contact.title}</h2></div>
        </div>
        <div className="grid gap-12 lg:grid-cols-[.72fr_1.28fr] lg:gap-20">
          <div>
            <p className="max-w-md text-base leading-7 text-muted">{data.contact.body}</p>
            <div className="mt-10 border-t border-line">
              <div className="flex justify-between border-b border-line py-4 font-mono text-[10px] uppercase tracking-[.08em]"><span className="text-muted">Base</span><span className="text-textmain">{data.contact.location}</span></div>
              <div className="flex justify-between border-b border-line py-4 font-mono text-[10px] uppercase tracking-[.08em]"><span className="text-muted">Response</span><span className="text-accent">Usually within 48h</span></div>
            </div>
            <div className="mt-7 flex flex-wrap gap-5">
              {data.contact.socialLinks.map(({ label, href }) => <a key={label} href={href} target="_blank" rel="noreferrer" className="font-mono text-[10px] uppercase tracking-[.1em] text-muted underline decoration-line underline-offset-4 hover:text-accent">{label} ↗</a>)}
            </div>
          </div>

          <form className="editorial-card p-6 md:p-8" onSubmit={submitContactForm}>
            <div className="mb-7 flex items-center justify-between border-b border-line pb-4"><span className="eyebrow">Message payload</span><span className="font-mono text-[9px] text-muted">POST /contact</span></div>
            <div className="grid gap-5 md:grid-cols-2">
              {data.contact.fields.map((field) => (
                <label key={field.id} htmlFor={field.id} className={field.type === "textarea" ? "md:col-span-2" : ""}>
                  <span className="mb-2 block font-mono text-[10px] uppercase tracking-[.1em] text-muted">{field.label}</span>
                  {field.type === "textarea" ? (
                    <textarea id={field.id} name={field.id} rows={field.rows} value={contactValues[field.id] || ""} onChange={(event) => updateContact(field.id, event.target.value)} required minLength={field.id === "message" ? 10 : undefined} maxLength={field.id === "message" ? 5000 : 120} className="field-control resize-y" />
                  ) : (
                    <input id={field.id} name={field.id} type={field.type} value={contactValues[field.id] || ""} onChange={(event) => updateContact(field.id, event.target.value)} required maxLength={field.id === "email" ? 254 : field.id === "subject" ? 200 : 120} className="field-control" />
                  )}
                </label>
              ))}
            </div>
            <div className="absolute left-[-10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="contact_guard_7f4c">Leave this field empty</label>
              <input id="contact_guard_7f4c" name="contact_guard_7f4c" type="text" tabIndex={-1} autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-bwignore="true" value={contactValues.contact_guard_7f4c} onChange={(event) => updateContact("contact_guard_7f4c", event.target.value)} />
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button type="submit" disabled={contactBusy} className="btn-primary min-w-40 disabled:cursor-not-allowed disabled:opacity-60">
                {contactBusy && <span aria-hidden="true" className="mr-2 h-3 w-3 animate-spin rounded-full border border-current border-r-transparent" />}
                {contactBusy ? "Transmitting…" : data.contact.buttonLabel}
              </button>
              {contactError && <p role="alert" className="text-sm text-red-400">{contactError}</p>}
              {contactMessage && <p role="status" className="font-mono text-[10px] uppercase tracking-wide text-accent">{contactMessage}</p>}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home2;
